import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Calendar, MessageCircle, Music } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
} from "@/components/ui/carousel";

const Events = () => {
  const parallaxRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const { data: upcomingEvents = [] } = useQuery({
    queryKey: ["events-homepage"],
    queryFn: async () => {
      const today = new Date().toISOString().split("T")[0];
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("is_active", true)
        .gte("date", today)
        .order("date", { ascending: true })
        .order("time", { ascending: true, nullsFirst: true })
        .limit(6);
      if (error) throw error;
      return data ?? [];
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current && sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const windowH = window.innerHeight;
        if (rect.bottom > 0 && rect.top < windowH) {
          const progress = (windowH - rect.top) / (windowH + rect.height);
          parallaxRef.current.style.transform = `translateY(${(progress - 0.5) * -250}px)`;
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <section id="events" className="py-24 px-4 bg-secondary/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-center text-3xl md:text-4xl font-semibold text-primary mb-2">
            Events & Musik
          </h2>
          <h3 className="text-center text-lg md:text-xl font-light text-primary/80 mb-12 tracking-[0.2em]">
            Dein Abend im Luther Club
          </h3>

          {upcomingEvents.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-10 h-10 text-primary/30 mx-auto mb-3" />
              <p className="text-muted-foreground">Neue Events werden bald angekündigt!</p>
            </div>
          ) : (
            <div className="px-0 md:px-4">
              <Carousel opts={{ align: "start" }} className="w-full">
                <CarouselContent className="-ml-4">
                  {upcomingEvents.map((event) => (
                    <CarouselItem
                      key={event.id}
                      className="pl-4 basis-[80%] sm:basis-1/2 lg:basis-1/3"
                    >
                      <div className="group border border-border/40 rounded-lg overflow-hidden bg-card/60 hover:border-primary/40 transition-all duration-300 h-full flex flex-col">
                        {/* Image – Instagram 4:5 */}
                        <div className="aspect-[4/5] bg-muted/20 relative overflow-hidden">
                          {event.image_url ? (
                            <img
                              src={event.image_url}
                              alt={event.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="font-[Montserrat] text-[10px] text-muted-foreground tracking-wider uppercase">
                                Event-Foto
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="p-5 flex-1 flex flex-col">
                          {(() => {
                            const d = new Date(event.date + "T00:00:00");
                            const weekday = d.toLocaleDateString("de-DE", { weekday: "short" }).replace(".", "").toUpperCase();
                            const date = d.toLocaleDateString("de-DE", { day: "2-digit", month: "long" });
                            return (
                              <div className="mb-3 inline-flex items-center gap-2 self-start border-l-2 border-primary pl-3 py-1">
                                <span className="font-[Montserrat] text-[10px] tracking-[0.18em] uppercase text-primary/70">{weekday}</span>
                                <span className="font-[Montserrat] text-sm tracking-[0.08em] uppercase text-primary">{date}</span>
                              </div>
                            );
                          })()}
                          <h4 className="font-[Montserrat] text-sm tracking-[0.1em] uppercase text-primary mb-1">
                            {event.title}
                          </h4>
                          {event.dj && (
                            <div className="flex items-center gap-1.5 mb-3">
                              <Music className="w-3.5 h-3.5 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">{event.dj}</span>
                            </div>
                          )}
                          {event.description && (
                            <p className="text-sm text-foreground/70 leading-relaxed flex-1">
                              {event.description.length > 80
                                ? `${event.description.slice(0, 80)}…`
                                : event.description}
                            </p>
                          )}
                          <Link
                            to={`/events#event-${event.id}`}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors mt-2 font-medium tracking-wide uppercase"
                            style={{ fontFamily: "'Montserrat', sans-serif" }}
                          >
                            Mehr lesen →
                          </Link>
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselNext className="border-primary/30 text-primary hover:bg-primary/10 hover:text-primary -right-4 md:-right-12" />
              </Carousel>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
            <Link
              to="/events"
              className="inline-flex items-center gap-2 font-[Montserrat] text-sm tracking-[0.15em] uppercase px-8 py-3 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
            >
              Alle Events ansehen
            </Link>
            <a
              href="https://whatsapp.com/channel/0029VaEVimS8kyyNPXG2E81f"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-[Montserrat] text-sm tracking-[0.15em] uppercase px-8 py-3 border border-primary/40 text-primary rounded hover:bg-primary/10 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp-Kanal
            </a>
          </div>
        </div>
      </section>

      {/* Parallax divider after Events */}
      <div
        ref={sectionRef}
        className="relative h-[400px] md:h-[500px] overflow-hidden border-y border-border/30"
      >
        <div
          ref={parallaxRef}
          className="absolute -inset-[150px] bg-cover bg-center will-change-transform"
          style={{ backgroundImage: "url('/images/parallax-2.webp')" }}
        />
        <div className="absolute inset-0 bg-background/30" />
      </div>
    </>
  );
};

export default Events;
